import { atomFamily, loadable } from "jotai/utils";
import { atom } from "jotai";
import { Team } from "@zapier/permissions";
import { Account, TeamWithRole } from "../types";
import getCDNImageUrl from "../../../canvas/src/utils/getCDNImageUrl";
import { accountAdminCountAtomFamily } from "./accountAdminCountAtomFamily";

type TeamsAtomFamilyProps = {
  currentAccountId: number | undefined;
  search: string | undefined;
  allowTeams: boolean;
  /* Account has multiple teams. You'll get a 403 when fetching /teams if this is false.
  See Slack thread: https://zapier.slack.com/archives/C024Z8BHJ/p1713208555005719?thread_ts=1712958519.463149&cid=C024Z8BHJ */
  hasMultipleTeams: boolean;
  account: Account | undefined;
};

type FetchResponse = {
  results: Team[];
};

// Base atom that suspends
const teamsAtomFamily_SUSPENDS = atomFamily(
  ({
    currentAccountId,
    search,
    allowTeams,
    hasMultipleTeams,
    account,
  }: TeamsAtomFamilyProps) => {
    const debugLabel = `teamsAtomFamily_SUSPENDS({ search: ${search} })`;

    const teamsAtom = atom<Promise<TeamWithRole[]>>(async (get) => {
      if (!allowTeams || !hasMultipleTeams) {
        return [];
      }

      const params = {
        limit: "10",
        offset: "0",
        search,
      };
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== undefined),
      ) as Record<string, string>;
      const query = new URLSearchParams(filteredParams);

      const res = await fetch(
        `/api/v4/accounts/${currentAccountId}/teams?${query}`,
      );

      if (!res.ok) {
        throw new Error("Failed to fetch teams");
      }

      const response: FetchResponse = await res.json();

      const teams: TeamWithRole[] = response.results || [];

      // Inject "Account admins" entry whenever user searches
      if (search && account) {
        const adminCountResult = get(
          accountAdminCountAtomFamily(currentAccountId),
        );

        // Get the count from loadable result
        const adminCount =
          adminCountResult.state === "hasData" ? adminCountResult.data : 0;

        const adminTeam: TeamWithRole = {
          id: -1,
          name: "Account admins",
          member_count: adminCount,
          logo: getCDNImageUrl("account-default-image.svg"),
          role: "admin",
        };
        teams.unshift(adminTeam);
      }

      return teams;
    });

    teamsAtom.debugLabel = debugLabel;
    return teamsAtom;
  },
  isEqual,
);

// Does NOT suspend due to loadable usage
export const teamsAtomFamily = atomFamily((props: TeamsAtomFamilyProps) => {
  const baseAtom = teamsAtomFamily_SUSPENDS(props);
  return loadable(baseAtom);
}, isEqual);

function isEqual(a: TeamsAtomFamilyProps, b: TeamsAtomFamilyProps) {
  return a.search === b.search && a.account?.id === b.account?.id;
}
